"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { useParams } from "next/navigation";
import { useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { InfoHint } from "@/components/ui/info-hint";
import {
  PluginIcon,
  DownloadIcon,
  StarIcon,
  OrganizationsIcon,
  UsersIcon,
  EditIcon,
  LinkIcon,
  ArrowRightIcon,
} from "@/components/icons";
import { EditProfileModal } from "@/components/profile/EditProfileModal";

interface BmMarketplaceItem {
  name: string;
  priceInCents: number;
  productType: string;
  serverName?: string;
  organizationName?: string;
  serverSlug?: string;
}

interface BmProfileData {
  marketplaceItems?: BmMarketplaceItem[];
  error?: string;
}

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatProductTypeLabel(value: string): string {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

export default function ProfilePage() {
  const params = useParams();
  const username = params.username as string;

  // Convex queries
  const profileUser = useQuery(api.users.getByUsername, { username });
  const currentUser = useQuery(api.users.getCurrentUser);

  const plugins = useQuery(
    api.plugins.getByUploader,
    profileUser ? { uploaderId: profileUser._id! } : "skip"
  );
  const organizations = useQuery(
    api.organizations.getPublicByOwner,
    profileUser ? { ownerId: profileUser._id! } : "skip"
  );

  // Determine if viewing own profile
  const isOwnProfile =
    currentUser && profileUser && currentUser._id === profileUser._id;

  // Edit profile modal
  const [editModalOpen, setEditModalOpen] = useState(false);

  // BM marketplace items (cross-service fetch)
  const [bmItems, setBmItems] = useState<BmMarketplaceItem[]>([]);
  const [bmLoading, setBmLoading] = useState(false);
  const [bmError, setBmError] = useState<string | null>(null);

  useEffect(() => {
    if (!profileUser?.username) return;

    const bmApiUrl = import.meta.env.VITE_BM_API_URL;
    if (!bmApiUrl) {
      setBmItems([]);
      return;
    }

    let cancelled = false;
    setBmLoading(true);
    setBmError(null);

    fetch(
      `${bmApiUrl}/api/public-profile?username=${encodeURIComponent(profileUser.username ?? "")}`
    )
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load marketplace data`);
        return res.json();
      })
      .then((data: BmProfileData) => {
        if (cancelled) return;
        setBmItems(data.marketplaceItems ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        setBmError(err instanceof Error ? err.message : "Failed to load marketplace data");
        setBmItems([]);
      })
      .finally(() => {
        if (!cancelled) setBmLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [profileUser?.username]);

  // Loading state
  if (profileUser === undefined) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Navbar />
        <main className="flex-1 px-4 py-8">
          <div className="mx-auto max-w-4xl space-y-8">
            {/* Header skeleton */}
            <div className="flex items-center gap-6">
              <Skeleton className="h-24 w-24 rounded-full" />
              <div className="space-y-3">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-5 w-72" />
                <Skeleton className="h-5 w-32" />
              </div>
            </div>
            {/* Sections skeleton */}
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Not found state
  if (profileUser === null) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Navbar />
        <main className="flex flex-1 items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md">
            <CardContent className="py-12 text-center">
              <h2 className="text-xl font-semibold text-foreground">
                User Not Found
              </h2>
              <p className="mt-2 text-muted">
                The user &quot;{username}&quot; doesn&apos;t exist or their
                profile is not available.
              </p>
              <Link href="/" className="mt-6 inline-block">
                <Button>Go Home</Button>
              </Link>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  const displayName = profileUser.name || profileUser.username || "User";
  const avatarSrc = profileUser.avatarUrl || profileUser.image;
  const bmApiUrl = import.meta.env.VITE_BM_API_URL;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />

      <main className="flex-1">
        <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
          {/* Profile Header */}
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-center gap-6">
              <Avatar className="h-24 w-24">
                <AvatarImage src={avatarSrc} alt={displayName} />
                <AvatarFallback className="text-2xl">
                  {displayName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>

              <div>
                <h1 className="text-3xl font-bold text-foreground">
                  {displayName}
                </h1>
                {profileUser.username && (
                  <p className="mt-0.5 text-muted">@{profileUser.username}</p>
                )}
                {profileUser.bio && (
                  <p className="mt-2 max-w-md text-sm text-foreground/80">
                    {profileUser.bio}
                  </p>
                )}
                {profileUser.showMemberSince !== false && profileUser.createdAt && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Badge variant="secondary">
                      Member since {formatDate(profileUser.createdAt)}
                    </Badge>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isOwnProfile && (
                <Button
                  variant="outline"
                  onClick={() => setEditModalOpen(true)}
                >
                  <EditIcon className="mr-2 h-4 w-4" />
                  Edit Profile
                </Button>
              )}
              {bmApiUrl && (
                <Link
                  href={`${bmApiUrl}/profile/${profileUser.username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline">
                    <LinkIcon className="mr-2 h-4 w-4" />
                    View on BlockyMarketplace
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Plugins Section */}
          {profileUser.showPlugins !== false && (
            <section className="mt-10">
              <h2 className="flex items-center gap-2 text-xl font-semibold text-foreground">
                <PluginIcon className="h-5 w-5" />
                Plugins
              </h2>

              {plugins === undefined ? (
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} className="h-32" />
                  ))}
                </div>
              ) : plugins.length === 0 ? (
                <Card className="mt-4">
                  <CardContent className="py-8 text-center">
                    <PluginIcon className="mx-auto h-10 w-10 text-muted" />
                    <p className="mt-2 text-muted">
                      No plugins uploaded yet.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  {plugins.map((plugin) => (
                    <Link
                      key={plugin._id}
                      href={`/plugins/${plugin.slug}`}
                    >
                      <Card className="h-full transition-colors hover:border-foreground/20">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base">
                            {plugin.name}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="line-clamp-2 text-sm text-muted">
                            {plugin.shortDescription}
                          </p>
                          <div className="mt-3 flex items-center gap-4 text-xs text-muted">
                            <span className="flex items-center gap-1">
                              <DownloadIcon className="h-3.5 w-3.5" />
                              {plugin.downloadCount}
                            </span>
                            <span className="flex items-center gap-1">
                              <StarIcon className="h-3.5 w-3.5" />
                              {plugin.starCount}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Organizations Section */}
          {profileUser.showOrganizations !== false && (
            <section className="mt-10">
              <h2 className="flex items-center gap-2 text-xl font-semibold text-foreground">
                <OrganizationsIcon className="h-5 w-5" />
                Organizations
              </h2>

              {organizations === undefined ? (
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  {[...Array(2)].map((_, i) => (
                    <Skeleton key={i} className="h-32" />
                  ))}
                </div>
              ) : organizations.length === 0 ? (
                <Card className="mt-4">
                  <CardContent className="py-8 text-center">
                    <OrganizationsIcon className="mx-auto h-10 w-10 text-muted" />
                    <p className="mt-2 text-muted">
                      No organizations yet.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  {organizations.map((org) => (
                    <Link key={org._id} href={`/org/${org.slug}`}>
                      <Card className="h-full transition-colors hover:border-foreground/20">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base">
                            {org.name}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {org.description && (
                            <p className="line-clamp-2 text-sm text-muted">
                              {org.description}
                            </p>
                          )}
                          <div className="mt-3 flex items-center gap-1 text-xs text-muted">
                            <UsersIcon className="h-3.5 w-3.5" />
                            {org.memberCount}{" "}
                            {org.memberCount === 1 ? "member" : "members"}
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Marketplace Items Section (from BlockyMarketplace) */}
          {profileUser.showMarketplaceItems !== false && bmApiUrl && (
            <section className="mt-10">
              <h2 className="flex items-center gap-2 text-xl font-semibold text-foreground">
                <ArrowRightIcon className="h-5 w-5" />
                Marketplace Items
                <InfoHint text="Products from your connected BlockyMarketplace profile." />
              </h2>
              <p className="mt-1 text-sm text-muted">
                From BlockyMarketplace
              </p>

              {bmLoading ? (
                <div className="mt-4 space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-16" />
                  ))}
                </div>
              ) : bmError ? (
                <Card className="mt-4">
                  <CardContent className="py-8 text-center">
                    <p className="text-sm text-muted">
                      Could not load marketplace items.
                    </p>
                  </CardContent>
                </Card>
              ) : bmItems.length === 0 ? (
                <Card className="mt-4">
                  <CardContent className="py-8 text-center">
                    <p className="text-muted">
                      No marketplace items listed.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="mt-4 space-y-3">
                  {bmItems.map((item, index) => (
                    <Card key={index}>
                      <CardContent className="flex items-center justify-between gap-4 p-4">
                        <div className="min-w-0">
                          <p className="truncate font-medium text-foreground">
                            {item.name}
                          </p>
                          <div className="mt-1 space-y-1 text-sm text-muted">
                            <p>
                              <span className="font-medium text-foreground/80">
                                Product Type:
                              </span>{" "}
                              {formatProductTypeLabel(item.productType)}
                            </p>
                            {item.serverName && (
                              <p>
                                <span className="font-medium text-foreground/80">
                                  Server:
                                </span>{" "}
                                {item.serverName}
                              </p>
                            )}
                            {item.organizationName &&
                              item.organizationName !== item.serverName && (
                                <p>
                                  <span className="font-medium text-foreground/80">
                                    Organization:
                                  </span>{" "}
                                  {item.organizationName}
                                </p>
                              )}
                          </div>
                        </div>
                        <Badge variant="secondary">
                          {item.priceInCents === 0
                            ? "Free"
                            : `$${(item.priceInCents / 100).toFixed(2)}`}
                        </Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Back link */}
          <div className="mt-12">
            <Link href="/">
              <Button variant="outline">
                <ArrowRightIcon className="mr-2 h-4 w-4 rotate-180" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </main>

      {/* Edit Profile Modal */}
      {isOwnProfile && profileUser && (
        <EditProfileModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          currentUser={profileUser}
        />
      )}

      <Footer />
    </div>
  );
}
